'use client';

import React from 'react';
import { Button } from '../ui/button';

interface ErrorDetail {
    message: string;
    name: string;
    stack?: string;
    componentStack?: string;
    timestamp: string;
    url?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error?: ErrorDetail;
    copySuccess: boolean;
}

class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    ErrorBoundaryState
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false, copySuccess: false };
    }

    static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        const detail: ErrorDetail = {
            name: error.name,
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack ?? undefined,
            timestamp: new Date().toISOString(),
            url:
                typeof window !== 'undefined'
                    ? window.location.href
                    : undefined,
        };

        this.setState({ error: detail });
        console.error('[ErrorBoundary]', detail);
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: undefined,
            copySuccess: false,
        });
    };

    handleCopy = async () => {
        const { error } = this.state;
        if (!error) return;
        const text = [
            `Time: ${error.timestamp}`,
            `URL: ${error.url}`,
            `Error: ${error.name}: ${error.message}`,
            `Stack:\n${error.stack}`,
            `Component Stack:\n${error.componentStack}`,
        ].join('\n\n');

        await navigator.clipboard.writeText(text);
        this.setState({ copySuccess: true });
        setTimeout(() => this.setState({ copySuccess: false }), 2000);
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const { error, copySuccess } = this.state;

        return (
            <div className='bg-background text-foreground flex min-h-screen w-screen items-center justify-center p-4'>
                <div className='w-full max-w-xl space-y-5'>
                    {/* Icon + heading */}
                    <div className='space-y-2'>
                        <div className='flex items-center gap-3'>
                            <span className='text-destructive text-3xl'>⚠</span>
                            <h1 className='text-xl font-semibold'>
                                Đã xảy ra lỗi
                            </h1>
                        </div>
                        <p className='text-muted-foreground text-sm'>
                            Ứng dụng gặp lỗi không mong muốn. Thử tải lại hoặc
                            liên hệ admin nếu lỗi tiếp tục.
                        </p>
                    </div>

                    {/* Error detail box */}
                    {error && (
                        <div className='border-destructive/30 bg-destructive/5 overflow-hidden rounded-lg border text-sm'>
                            {/* Header row */}
                            <div className='border-destructive/20 bg-destructive/10 flex items-center justify-between border-b px-4 py-2'>
                                <span className='text-destructive font-mono font-medium'>
                                    {error.name}
                                </span>
                                <span className='text-muted-foreground text-xs'>
                                    {new Date(
                                        error.timestamp
                                    ).toLocaleTimeString('vi-VN')}
                                </span>
                            </div>

                            {/* Message */}
                            <div className='border-destructive/10 border-b px-4 py-3'>
                                <p className='text-foreground font-mono wrap-break-word'>
                                    {error.message}
                                </p>
                            </div>

                            {/* Stack trace */}
                            {error.stack && (
                                <details className='group'>
                                    <summary className='text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-2 px-4 py-2 transition-colors select-none'>
                                        <span className='text-xs'>▶</span>
                                        <span>Stack trace</span>
                                    </summary>
                                    <pre className='text-muted-foreground max-h-48 overflow-x-auto overflow-y-auto px-4 pb-4 font-mono text-xs wrap-break-word whitespace-pre-wrap'>
                                        {error.stack}
                                    </pre>
                                </details>
                            )}

                            {/* Component stack */}
                            {error.componentStack && (
                                <details>
                                    <summary className='text-muted-foreground hover:text-foreground border-destructive/10 flex cursor-pointer items-center gap-2 border-t px-4 py-2 transition-colors select-none'>
                                        <span className='text-xs'>▶</span>
                                        <span>Component stack</span>
                                    </summary>
                                    <pre className='text-muted-foreground max-h-40 overflow-x-auto overflow-y-auto px-4 pb-4 font-mono text-xs wrap-break-word whitespace-pre-wrap'>
                                        {error.componentStack}
                                    </pre>
                                </details>
                            )}
                        </div>
                    )}

                    {/* URL */}
                    {error?.url && (
                        <p className='text-muted-foreground truncate font-mono text-xs'>
                            {error.url}
                        </p>
                    )}

                    {/* Actions */}
                    <div className='flex flex-wrap gap-2'>
                        <Button onClick={this.handleReset}>Thử lại</Button>
                        <Button variant='outline' onClick={this.handleCopy}>
                            {copySuccess ? '✓ Đã copy' : 'Copy lỗi'}
                        </Button>
                        <Button
                            variant='destructive'
                            onClick={() => window.location.reload()}
                        >
                            Tải lại trang
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
